import { useState } from "react";
import { TextField } from "@/components/TextField";
import { SelectField } from "@/components/SelectField";
import { ClassCodeField } from "@/components/ClassCodeField";
import { FieldMarker } from "@/components/FieldMarker";
import type { GroupItemField, ManifestField } from "@/lib/brokerProducts";
import type { CodeDisplayRef, CodeOption } from "@/lib/brokerSubmissions";

/** Resolved class-code display refs (PMS-S150): canonical Code id → {code,
 *  description}, so a prefilled classCode id renders as its code, not the uuid. */
export type CodeRefs = Record<string, CodeDisplayRef>;

// BRK-S052 — the generic manifest field renderer. Maps every product-manifest
// field type to the existing shared inputs, so the submission details step is
// fully manifest-driven (no product- or WC-specific branches anywhere in the
// UI). Controlled against the submission FIELD BAG's value shapes:
//   text/date/select → string · number/currency → number · boolean → boolean
//   multi-select → string[] · classCode → the code id · group → row objects
// `undefined` means "absent from the bag" — how tri-state booleans and unset
// optionals stay unanswered rather than defaulted (the server applies manifest
// defaults at canonical assembly; the SPA never writes them into the bag).

/** A scalar bag value. */
export type BagScalar = string | number | boolean | string[];
/** One repeatable-group row: item-field key → scalar. */
export type BagRow = Record<string, BagScalar | undefined>;
/** Any bag value a manifest field produces. */
export type BagValue = BagScalar | BagRow[];
/** The submission field bag: canonical field key → value. */
export type FieldBag = Record<string, BagValue | undefined>;

export function FieldRenderer({
  field,
  value,
  onChange,
  required,
  codeRefs,
  submissionId,
}: {
  field: ManifestField;
  value: BagValue | undefined;
  onChange: (value: BagValue | undefined) => void;
  required?: boolean;
  /** Resolved class-code refs (PMS-S150) for rendering prefilled ids (BRK-S062). */
  codeRefs?: CodeRefs;
  /** The submission the class-code typeahead is scoped to (PMS-S156/BRK-S065). */
  submissionId: string;
}) {
  if (field.type === "group") {
    return (
      <GroupField
        field={field}
        value={Array.isArray(value) ? (value as BagRow[]) : undefined}
        onChange={onChange}
        required={required}
        codeRefs={codeRefs}
        submissionId={submissionId}
      />
    );
  }
  return (
    <ScalarControl
      field={field}
      value={value as BagScalar | undefined}
      onChange={onChange}
      required={required}
      codeRefs={codeRefs}
      submissionId={submissionId}
    />
  );
}

// ─── Scalar controls (shared by top-level fields and group item fields) ───────

interface ScalarFieldShape {
  key: string;
  label: string;
  type: ManifestField["type"];
  options?: { value: string; label: string }[];
  multiple?: boolean;
  defaultValue?: string | number | boolean | string[];
  helpText?: string;
}

function ScalarControl({
  field,
  value,
  onChange,
  required,
  codeRefs,
  submissionId,
}: {
  field: ScalarFieldShape;
  value: BagScalar | undefined;
  onChange: (value: BagScalar | undefined) => void;
  required?: boolean;
  codeRefs?: CodeRefs;
  submissionId: string;
}) {
  const help = field.helpText ? (
    <p className="text-xs text-brk-dim">{field.helpText}</p>
  ) : null;

  switch (field.type) {
    case "text":
      return (
        <div className="flex flex-col gap-1">
          <TextField
            label={field.label}
            value={(value as string | undefined) ?? ""}
            onChange={(e) =>
              onChange(e.target.value === "" ? undefined : e.target.value)
            }
            required={required}
          />
          {help}
        </div>
      );
    case "date":
      return (
        <div className="flex flex-col gap-1">
          <TextField
            label={field.label}
            type="date"
            value={(value as string | undefined) ?? ""}
            onChange={(e) =>
              onChange(e.target.value === "" ? undefined : e.target.value)
            }
            required={required}
          />
          {help}
        </div>
      );
    case "number":
    case "currency":
      return (
        <div className="flex flex-col gap-1">
          <TextField
            label={field.label}
            type="number"
            value={(value as number | undefined) ?? ""}
            placeholder={
              field.defaultValue != null
                ? `Defaults to ${Number(field.defaultValue).toLocaleString("en-US")}`
                : undefined
            }
            onChange={(e) => {
              const n = Number(e.target.value);
              onChange(
                e.target.value === "" || Number.isNaN(n) ? undefined : n,
              );
            }}
            required={required}
          />
          {help}
        </div>
      );
    case "select":
      if (field.multiple) {
        return (
          <MultiSelect
            field={field}
            value={(value as string[] | undefined) ?? []}
            onChange={onChange}
            required={required}
          />
        );
      }
      return (
        <div className="flex flex-col gap-1">
          <SelectField
            label={field.label}
            placeholder="Select…"
            options={field.options ?? []}
            value={(value as string | undefined) ?? ""}
            onChange={(e) =>
              onChange(e.target.value === "" ? undefined : e.target.value)
            }
            required={required}
          />
          {help}
        </div>
      );
    case "boolean":
      // Tri-state: unanswered (absent) / Yes / No — a checkbox can't say
      // "unanswered", so booleans are a select like the ACORD forms.
      return (
        <div className="flex flex-col gap-1">
          <SelectField
            label={field.label}
            placeholder="Select…"
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
            value={value === true ? "yes" : value === false ? "no" : ""}
            onChange={(e) =>
              onChange(
                e.target.value === "" ? undefined : e.target.value === "yes",
              )
            }
            required={required}
          />
          {help}
        </div>
      );
    case "classCode":
      return (
        <ClassCodeValue
          label={field.label}
          value={value as string | undefined}
          onChange={onChange}
          required={required}
          help={help}
          codeRefs={codeRefs}
          submissionId={submissionId}
        />
      );
    case "group":
      // Nested groups aren't in the contract (group items are scalars).
      return null;
  }
}

// The class-code typeahead stores the CODE ID in the bag; the selected option
// is cached locally so the chip shows the human code/name. A prefilled id is
// resolved from the submission's display refs (PMS-S150/BRK-S062) — carried-
// forward exposures show the code + description instead of the raw uuid. An id
// with neither a cached option nor a ref degrades to a bare-id chip (never a
// crash — the bag itself carries no labels).
function ClassCodeValue({
  label,
  value,
  onChange,
  required,
  help,
  codeRefs,
  submissionId,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  required?: boolean;
  help: React.ReactNode;
  codeRefs?: CodeRefs;
  submissionId: string;
}) {
  const [selected, setSelected] = useState<CodeOption | null>(null);
  const ref = value ? codeRefs?.[value] : undefined;
  const display: CodeOption | null = value
    ? selected && selected.id === value
      ? selected
      : ref
        ? {
            id: ref.id,
            code: ref.code,
            typeName: "",
            friendlyName: null,
            description: ref.description,
          }
        : {
            id: value,
            code: value,
            typeName: "",
            friendlyName: null,
            description: null,
          }
    : null;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-baseline gap-1.5">
        <span className="text-sm font-medium text-brk-text">{label}</span>
        <FieldMarker required={required} />
      </span>
      <ClassCodeField
        value={display}
        submissionId={submissionId}
        onChange={(code) => {
          setSelected(code);
          onChange(code?.id);
        }}
      />
      {help}
    </div>
  );
}

// Multi-select renders as a checkbox list (a native multi-<select> is easy to
// mis-tap on mobile); the bag value is the checked option values, in the
// manifest's option order.
function MultiSelect({
  field,
  value,
  onChange,
  required,
}: {
  field: ScalarFieldShape;
  value: string[];
  onChange: (value: BagScalar | undefined) => void;
  required?: boolean;
}) {
  const options = field.options ?? [];
  function toggle(option: string) {
    const next = value.includes(option)
      ? value.filter((v) => v !== option)
      : options
          .map((o) => o.value)
          .filter((v) => value.includes(v) || v === option);
    onChange(next.length ? next : undefined);
  }
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="flex items-baseline gap-1.5 text-sm font-medium text-brk-text">
        {field.label}
        <FieldMarker required={required} />
      </legend>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:gap-x-4">
        {options.map((o) => (
          <label
            key={o.value}
            className="flex items-center gap-2 text-sm text-brk-text"
          >
            <input
              type="checkbox"
              checked={value.includes(o.value)}
              onChange={() => toggle(o.value)}
            />
            {o.label}
          </label>
        ))}
      </div>
      {field.helpText && (
        <p className="text-xs text-brk-dim">{field.helpText}</p>
      )}
    </fieldset>
  );
}

// ─── Repeatable group ──────────────────────────────────────────────────────────

// The repeated-row pattern from the WC exposures table (BRK-S047), generalized:
// rows stack on mobile and go inline on sm+, each row renders the group's item
// fields, the sole row has no remove control, and the list scrolls in its own
// container so it never forces page overflow.
function GroupField({
  field,
  value,
  onChange,
  required,
  codeRefs,
  submissionId,
}: {
  field: ManifestField;
  value: BagRow[] | undefined;
  onChange: (value: BagValue | undefined) => void;
  required?: boolean;
  codeRefs?: CodeRefs;
  submissionId: string;
}) {
  // Honor the manifest's `minItems` (default 1): a required group seeds its
  // minimum rows and can't be emptied below it, while an OPTIONAL group
  // (minItems 0, e.g. the commercial-auto driver schedule) starts empty and
  // every row — including the last — is removable. Generic: driven by the
  // field, never the product.
  const min = field.minItems ?? 1;
  const rows: BagRow[] =
    value && value.length > 0 ? value : Array.from({ length: min }, () => ({}));
  // Stable row keys so per-row internal state (the class-code search) survives
  // removes; synced to row count (adds append, removes splice).
  const [keys, setKeys] = useState<number[]>(() => rows.map((_, i) => i));
  const [nextKey, setNextKey] = useState(rows.length);
  if (keys.length !== rows.length) {
    // External length change (e.g. a prefill landing after mount): re-key.
    setKeys(rows.map((_, i) => nextKey + i));
    setNextKey(nextKey + rows.length);
    return null;
  }

  function patch(
    index: number,
    itemKey: string,
    itemValue: BagScalar | undefined,
  ) {
    const next = rows.map((row, i) => {
      if (i !== index) return row;
      const updated: BagRow = { ...row };
      if (itemValue === undefined) delete updated[itemKey];
      else updated[itemKey] = itemValue;
      return updated;
    });
    onChange(next);
  }

  function addRow() {
    setKeys((k) => [...k, nextKey]);
    setNextKey((k) => k + 1);
    onChange([...rows, {}]);
  }

  function removeRow(index: number) {
    setKeys((k) => k.filter((_, i) => i !== index));
    onChange(rows.filter((_, i) => i !== index));
  }

  const items: GroupItemField[] = field.item ?? [];

  return (
    <div className="flex flex-col gap-2">
      <span className="flex items-baseline gap-1.5">
        <span className="text-sm font-medium text-brk-text">{field.label}</span>
        <FieldMarker required={required} />
      </span>
      {field.helpText && (
        <p className="text-xs text-brk-dim">{field.helpText}</p>
      )}
      <div className="overflow-x-auto">
        <div className="flex flex-col gap-3">
          {rows.map((row, index) => (
            <div
              key={keys[index]}
              className="grid grid-cols-1 items-start gap-3 rounded-md border border-brk-border p-3 sm:grid-cols-2"
            >
              {items.map((item) => (
                <ScalarControl
                  key={item.key}
                  field={item}
                  value={row[item.key]}
                  onChange={(v) => patch(index, item.key, v)}
                  required={required && !item.optional}
                  codeRefs={codeRefs}
                  submissionId={submissionId}
                />
              ))}
              {rows.length > min && (
                <button
                  type="button"
                  aria-label={`Remove ${field.label.toLowerCase()} row`}
                  onClick={() => removeRow(index)}
                  className="justify-self-start text-sm text-brk-danger hover:underline"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={addRow}
        className="self-start text-sm text-brk-accent hover:underline"
      >
        + Add row
      </button>
    </div>
  );
}
